/** All possible roles in the WorkLynx system */
export type Role = 'ADMIN' | 'BO' | 'BM' | 'MANAGER' | 'EMPLOYEE';

/** Roles that exist within an organisation (excludes platform admin) */
export type OrgRole = Exclude<Role, 'ADMIN'>;

/** Role priority order: highest first */
export const ROLE_PRIORITY: OrgRole[] = ['BO', 'BM', 'MANAGER', 'EMPLOYEE'];

/** Human-readable role labels */
export const ROLE_LABELS: Record<Role, string> = {
  ADMIN: 'Platform Admin',
  BO: 'Business Owner',
  BM: 'Business Manager',
  MANAGER: 'Manager',
  EMPLOYEE: 'Employee',
};

/** Dashboard paths per role */
export const ROLE_DASHBOARD_PATHS: Record<Role, string> = {
  ADMIN: '/admin/dashboard',
  BO: '/bo/dashboard',
  BM: '/bm/dashboard',
  MANAGER: '/manager/dashboard',
  EMPLOYEE: '/employee/dashboard',
};

/** Authenticated users without an org membership land here */
/**
 * Where somebody goes when they are signed in but belong to no organisation. Their own
 * record is the one thing that is still theirs, and it carries the prompt to finish
 * setting up a profile for anyone who has never joined an organisation at all.
 *
 * This used to be the profile setup page, which read as being locked out to a person
 * whose engagement had simply ended.
 */
export const NO_ORG_REDIRECT_PATH = '/my-record';
export const PROFILE_SETUP_PATH = '/profile?setup=1';

/** Given an array of roles, return the highest-priority one */
export function pickHighestRole(roles: OrgRole[]): OrgRole | null {
  for (const r of ROLE_PRIORITY) {
    if (roles.includes(r)) return r;
  }
  return null;
}

/** Get the dashboard path for a given role */
export function dashboardPathForRole(role: Role): string {
  return ROLE_DASHBOARD_PATHS[role] ?? '/login';
}
